import { inject, Injectable, OnDestroy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NodeUsingColumn, WorkflowSerialized } from '@selfai-platform/pipeline-common';
import { WorkflowStateService } from '@selfai-platform/storage';
import { BehaviorSubject, combineLatest, filter, Observable } from 'rxjs';
import { collectAllValues } from '../../utils';
import { WorkflowVariablesStore } from '../store';

@Injectable()
export class VariablesUsedInWorkflowService
  extends BehaviorSubject<Map<string, NodeUsingColumn[]>>
  implements OnDestroy
{
  private readonly variablesStore = inject(WorkflowVariablesStore);
  private readonly workflowStateService = inject(WorkflowStateService);

  get usedInMap$(): Observable<Map<string, NodeUsingColumn[]>> {
    return this.asObservable();
  }

  constructor() {
    super(new Map());

    combineLatest([this.workflowStateService.getWorkflowState(), toObservable(this.variablesStore.variables)])
      .pipe(filter(([workflow, variables]) => !!workflow && !!variables))
      .subscribe(([workflow, variables]) => {
        const idPathPair = variables.map((variable) => ({
          id: variable.id,
          path: this.variablesStore.buildVariablePath(variable),
        }));
        this.calculateVariablesMap(workflow as WorkflowSerialized, idPathPair);
      });
  }

  ngOnDestroy(): void {
    this.complete();
  }

  private calculateVariablesMap(workflow: WorkflowSerialized, idPathPair: { id: string; path: string }[]): void {
    const columnsMap = new Map<string, NodeUsingColumn[]>();
    const cubesForSearch = workflow.workflow.nodes.map((cube) => {
      const result = collectAllValues(cube.parameters);

      return {
        id: cube.id,
        name: workflow.thirdPartyData.gui.nodes[cube.id]?.uiName || cube.operation?.name || '',
        searchText: result,
      };
    });
    for (const { id, path } of idPathPair) {
      const usedIn: NodeUsingColumn[] = [];
      cubesForSearch.forEach((cube) => {
        for (let i = 0; i < cube.searchText.length; i++) {
          if (cube.searchText[i].includes(`\${${path}}`)) {
            usedIn.push({
              nodeId: cube.id,
              title: cube.name,
            });

            return;
          }
        }
      });

      if (usedIn.length > 0) {
        columnsMap.set(id, usedIn);
      }
    }

    this.next(columnsMap);
  }
}
